import { Controller } from "@hotwired/stimulus"
import { computePosition, autoUpdate, flip, shift, offset } from "@floating-ui/dom"

/**
 * Client-side filter for preloaded account rows (e.g. task inline badges), or
 * debounced server search via Turbo frame reload (meeting account assignment).
 */
export default class extends Controller {
  static targets = ["input", "account", "noResults", "anchor", "menu"]
  static values = {
    editUrl: String,
    listFrameId: String,
    closeUrl: String,
    debounceMs: { type: Number, default: 500 }
  }

  connect() {
    if (this.useServerSearch) {
      this.focusInputAtEnd()
      this.setupFloatingMenu()
      if (this.hasCloseUrlValue) {
        this.handleOutsideClick = this.handleOutsideClick.bind(this)
        this.handleKeydown = this.handleKeydown.bind(this)
        document.addEventListener("click", this.handleOutsideClick)
        document.addEventListener("keydown", this.handleKeydown)
      }
    } else {
      this.query = ""
    }
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.stopFloatingMenu) {
      this.stopFloatingMenu()
      this.stopFloatingMenu = null
    }
    if (this.handleOutsideClick) {
      document.removeEventListener("click", this.handleOutsideClick)
      document.removeEventListener("keydown", this.handleKeydown)
    }
  }

  handleOutsideClick(event) {
    const frame = this.element.closest("turbo-frame")
    if (frame && !frame.contains(event.target)) {
      this.close()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  close() {
    if (!this.hasCloseUrlValue) return
    const frame = this.element.closest("turbo-frame")
    if (frame) frame.src = this.closeUrlValue
  }

  setupFloatingMenu() {
    if (!this.hasMenuTarget || !this.hasAnchorTarget) return

    this.stopFloatingMenu = autoUpdate(
      this.anchorTarget,
      this.menuTarget,
      () => this.updateFloatingMenuPosition(),
      { ancestorScroll: true, ancestorResize: true, elementResize: true }
    )
    this.updateFloatingMenuPosition()
  }

  async updateFloatingMenuPosition() {
    const { x, y } = await computePosition(this.anchorTarget, this.menuTarget, {
      placement: "bottom-start",
      strategy: "fixed",
      middleware: [
        offset(4),
        flip({ fallbackPlacements: ["top-start", "bottom-start"] }),
        shift({ padding: 8 })
      ]
    })

    Object.assign(this.menuTarget.style, {
      position: "fixed",
      left: `${x}px`,
      top: `${y}px`,
      zIndex: "1300",
      visibility: "visible"
    })
  }

  get useServerSearch() {
    return this.hasEditUrlValue && this.editUrlValue.length > 0 &&
      this.hasListFrameIdValue && this.listFrameIdValue.length > 0
  }

  scheduleSearch() {
    if (!this.useServerSearch) return
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchResults(), this.debounceMsValue)
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  fetchResults() {
    this.debounceTimer = null
    const url = new URL(this.editUrlValue, window.location.origin)
    const q = this.inputTarget.value.trim()
    if (q.length > 0) {
      url.searchParams.set('q', q)
    } else {
      url.searchParams.delete('q')
    }
    Turbo.visit(url.toString(), { frame: this.listFrameIdValue })
  }

  focusInputAtEnd() {
    if (!this.hasInputTarget) return

    const el = this.inputTarget
    el.focus()
    const len = el.value.length
    el.setSelectionRange(len, len)
  }

  search(event) {
    if (this.useServerSearch) return
    if (event.which === 13) event.preventDefault()

    this.query = event.target.value.toLowerCase()
    this.filterAccounts()
  }

  filterAccounts() {
    const query = this.query
    if (!this.hasAccountTarget) return

    this.accountTargets.forEach(el => {
      const textContent = el.textContent.toLowerCase()
      el.style.display = textContent.includes(query) ? '' : 'none'
    })

    if (this.hasNoResultsTarget) {
      const visibleAccounts = this.accountTargets.filter(el => el.style.display !== 'none')
      if (visibleAccounts.length === 0 && query.length > 0) {
        this.noResultsTarget.style.display = ''
      } else {
        this.noResultsTarget.style.display = 'none'
      }
    }
  }
}
